'use client';

import React, { useMemo, useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Link from 'next/link';
import { BrandIcon } from 'src/components';
import { useLayoutControl } from 'src/context/layout';
import { useLogging } from 'src/context/logging';
import { useGetMappingsQuery } from 'src/api/redux';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare,
  faCircleSmall,
  faUserTie,
  IconDefinition,
} from '@fortawesome/pro-solid-svg-icons';

interface CRMLinks {
  crm: string;
  crm_link: string;
  crm_object_type: string;
  crm_status?: string;
  mapping_id?: string;
  owner?: {
    firstname: string;
    lastname: string;
  };
}

interface CRMMaxMenuV2Props {
  provider: MedScout.Opportunity | null;
  integration: string;
  options: CRMLinks[];
  anchorEl: HTMLElement | null;
  setAnchorEl: (el: HTMLElement | null) => void;
}

/**
 * Map of CRM codes to display names
 */
const CRM_DISPLAY_NAMES: Record<string, string> = {
  salesforce: 'Salesforce',
  hubspot: 'HubSpot',
  pipedrive: 'Pipedrive',
  microsoftdynamics: 'Microsoft Dynamics',
  zohocrm: 'Zoho CRM',
  medrm: 'MedRM',
  third_party: 'CRM',
};

/**
 * Helper to get full name from owner object
 */
const getFullName = (owner?: {
  firstname: string;
  lastname: string;
}): string => {
  return `${owner?.firstname || ''} ${owner?.lastname || ''}`.trim();
};

/**
 * Reusable info row component with icon
 */
const InfoRow: React.FC<{
  icon: IconDefinition;
  text: string;
  className?: string;
}> = ({ icon, text, className = '' }) => (
  <div
    className={`flex items-center justify-start gap-2 px-4 py-1.5 ${className}`}
  >
    <div className="flex items-center justify-center w-4 shrink-0">
      <Icon icon={icon} className="text-secondary w-4 h-4" />
    </div>
    <span className="text-xs font-medium text-primary">{text}</span>
  </div>
);

/**
 * Push to New button component
 */
const PushToNewButton: React.FC<{
  onClick: () => void;
  disabled: boolean;
  integration: string;
}> = ({ onClick, disabled, integration }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`w-full flex items-center gap-2 px-4 py-2 text-left transition-all duration-300 ${
      disabled
        ? 'cursor-not-allowed opacity-50'
        : 'hover:bg-neutral-100 cursor-pointer'
    }`}
  >
    <BrandIcon
      brand={integration?.toLowerCase() || 'salesforce'}
      width="1.1rem"
      height="1rem"
    />
    <span className="text-sm font-medium text-sky-900">Push to New</span>
  </button>
);

/**
 * Single CRM link detail view component
 */
const SingleCRMLink: React.FC<{
  option: CRMLinks;
  onLinkClick: (e: React.MouseEvent) => void;
}> = ({ option, onLinkClick }) => {
  const {
    crm_object_type: object,
    crm_status: status,
    crm_link: link,
    owner,
  } = option;
  const fullName = getFullName(owner);

  return (
    <div className="w-44">
      {status && <InfoRow icon={faCircleSmall} text={status} />}
      {fullName && <InfoRow icon={faUserTie} text={fullName} />}
      {link && (
        <Link
          href={link}
          onClick={onLinkClick}
          target="_blank"
          className="flex items-center justify-start gap-2 px-4 py-1.5 no-underline hover:underline"
        >
          <div className="flex items-center justify-center w-4 shrink-0">
            <Icon
              icon={faArrowUpRightFromSquare}
              className="text-secondary w-4 h-4"
            />
          </div>
          <span className="text-xs font-medium text-primary">
            View {object}
          </span>
        </Link>
      )}
    </div>
  );
};

/**
 * Menu item component for multiple CRM links
 */
const CRMMenuItem: React.FC<{
  link: CRMLinks;
  onLinkClick: (e: React.MouseEvent) => void;
}> = ({ link, onLinkClick }) => {
  const fullName = getFullName(link.owner);
  const crmName = CRM_DISPLAY_NAMES[link.crm?.toLowerCase()] || 'CRM';
  const label = fullName
    ? `View in ${crmName} (Owner: ${fullName})`
    : `View in ${crmName}`;

  return (
    <div
      className="cursor-pointer hover:bg-neutral-100 transition-all duration-300 rounded-lg"
      aria-label={`Menu Item For ${label}`}
    >
      <Link
        href={link.crm_link}
        onClick={onLinkClick}
        target="_blank"
        className="flex items-center gap-2 px-4 py-2 w-full h-full no-underline"
      >
        <span className="text-sm font-medium text-sky-900">{label}</span>
      </Link>
    </div>
  );
};

/**
 * CRM Max Menu V2 dropdown using Tailwind and createPortal
 * Replaces MUI Menu component with custom-styled dropdown
 */
const CRMMaxMenuV2: React.FC<CRMMaxMenuV2Props> = ({
  provider,
  integration,
  options = [],
  anchorEl,
  setAnchorEl,
}): React.ReactElement | null => {
  const { setPushProviderToCRM } = useLayoutControl();
  const log = useLogging();
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  const { data: allMappings } = useGetMappingsQuery({
    sort: 'label',
    order: 'asc',
    page: 0,
    pageSize: 100,
  });

  // Gets mappings for specific provider type, removing used mappings
  const mappings = useMemo(() => {
    return allMappings?.results
      ?.filter(
        (mapping) =>
          mapping?.medscout_object_type ===
            provider?.content_type?.toUpperCase() &&
          mapping?.enabled &&
          mapping?.crm?.toLowerCase() === integration?.toLowerCase()
      )
      ?.filter(
        (mapping) =>
          !provider?.crm_links?.some(
            (link: any) => link.mapping_id === mapping.id
          )
      );
  }, [allMappings, integration, provider]);

  // Update dropdown position when anchorEl changes
  useEffect(() => {
    if (anchorEl && dropdownRef.current) {
      const rect = anchorEl.getBoundingClientRect();
      const dropdownWidth = dropdownRef.current.offsetWidth;

      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4, // 4px gap
        left: rect.right - dropdownWidth + window.scrollX,
      });
    }
  }, [anchorEl]);

  // Handle clicking outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        anchorEl &&
        !anchorEl.contains(event.target as Node)
      ) {
        setAnchorEl(null);
      }
    };

    if (anchorEl) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('resize', () => setAnchorEl(null));
      window.addEventListener('scroll', () => setAnchorEl(null));
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', () => setAnchorEl(null));
      window.removeEventListener('scroll', () => setAnchorEl(null));
    };
  }, [anchorEl, setAnchorEl]);

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLinkClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    log.event('viewInCRMLinkClicked');
    setAnchorEl(null);
  };

  const handlePushToNew = () => {
    handleMenuClose();
    setPushProviderToCRM(provider, integration);
  };

  if (!options.length || !anchorEl) return null;

  const isSingleOption = options.length === 1;

  const dropdownContent = (
    <div
      ref={dropdownRef}
      style={{
        position: 'absolute',
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        zIndex: 1300,
      }}
      className="bg-white shadow-lg rounded-lg border border-neutral-200 py-2 min-w-64"
      role="menu"
    >
      {isSingleOption ? (
        <SingleCRMLink option={options[0]} onLinkClick={handleLinkClick} />
      ) : (
        options.map((link, index) => (
          <CRMMenuItem key={index} link={link} onLinkClick={handleLinkClick} />
        ))
      )}

      <div
        className={`border-t border-neutral-200 ${
          isSingleOption ? 'mt-2 pt-2' : 'mt-1 pt-1'
        }`}
      >
        <PushToNewButton
          onClick={handlePushToNew}
          disabled={!mappings?.length}
          integration={integration}
        />
      </div>
    </div>
  );

  return createPortal(dropdownContent, document.body);
};

export default CRMMaxMenuV2;
